import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
    HStack,
    VStack,
    Text,
    Button,
    IconButton,
    Input,
    NumberInput,
    NumberInputField,
    Select,
    Box,
    Divider,
    Tooltip,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiCheck } from 'react-icons/fi';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';
import { useDimStyleStore, type DimensionStyle } from '../../tools/cad/DimensioningTools';

/**
 * Dimension Style Manager (2D DIMSTYLE dialog parity).
 * List of styles on the left (create/rename/delete/set-current), property
 * editor for the selected style on the right. All edits hit the persisted
 * dim-style store directly.
 */

const ARROW_TYPES = ['open', 'closed', 'dot', 'tick', 'none'] as const;

function NumberField({ label, value, onChange, step = 0.05 }: {
    label: string; value: number; onChange: (v: number) => void; step?: number;
}) {
    return (
        <HStack justify="space-between" spacing={3}>
            <Text fontSize="sm" color="var(--color-text-secondary)">{label}</Text>
            <NumberInput size="sm" maxW="110px" value={value} step={step} precision={3}
                onChange={(_, v) => onChange(Number.isFinite(v) ? v : value)}
            >
                <NumberInputField bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
            </NumberInput>
        </HStack>
    );
}

function ColorField({ label, value, onChange }: {
    label: string; value: string; onChange: (v: string) => void;
}) {
    return (
        <HStack justify="space-between" spacing={3}>
            <Text fontSize="sm" color="var(--color-text-secondary)">{label}</Text>
            <Input size="sm" maxW="110px" value={value} bg="var(--color-bg-primary)"
                borderColor="var(--color-border)" fontFamily="mono"
                onChange={(e) => onChange(e.target.value)}
            />
        </HStack>
    );
}

export function DimensionStylesDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'dimStyles');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const styles = useDimStyleStore((s) => s.styles);
    const activeStyleId = useDimStyleStore((s) => s.activeStyleId);
    const addStyle = useDimStyleStore((s) => s.addStyle);
    const updateStyle = useDimStyleStore((s) => s.updateStyle);
    const deleteStyle = useDimStyleStore((s) => s.deleteStyle);
    const setActiveStyle = useDimStyleStore((s) =>s.setActiveStyle); const [selectedId, setSelectedId] = useState<string>(activeStyleId); const ids = Object.keys(styles); const selected: DimensionStyle | undefined = styles[selectedId] ?? styles[activeStyleId]; if (!selected) return null; const isStandard = selected.id === 'standard'; const patch = (updates: Partial<DimensionStyle>) => updateStyle(selected.id, updates);

    const handleNew = () => {
        const base = styles[activeStyleId];
        let n = 1;
        while (styles[`copy-${n}`]) n += 1;
        const id = `copy-${n}`;
        addStyle({ ...base, id, name: `Style ${n}` });
        setSelectedId(id);
    };

    return (
        <ManageDialogFrame
            title={t('manage.dimensionStyleManager')}
            isOpen={isOpen}
            onClose={onClose}
            size="2xl"
            footer={<ManageDialogCloseFooter onClose={onClose} />}
        >
            <HStack spacing={4} align="stretch">
                {/* Style list column */}
                <VStack spacing={1} align="stretch" w="220px" flexShrink={0}>
                    <Box flex="1" overflowY="auto" maxH="420px"
                        borderWidth="1px" borderColor="var(--color-border)" borderRadius="var(--cad-radius-sm)" p={1}
                    >
                        {ids.map((id) => (
                            <HStack
                                key={id}
                                px={2}
                                py={1.5}
                                borderRadius="var(--cad-radius-sm)"
                                cursor="pointer"
                                bg={id === selected.id ? 'var(--color-accent)' : 'transparent'}
                                color={id === selected.id ? 'white' : 'var(--color-text-primary)'}
                                _hover={{ bg: id === selected.id ? 'var(--color-accent)' : 'var(--color-bg-hover, whiteAlpha.50)' }}
                                onClick={() => setSelectedId(id)}
                            >
                                {id === activeStyleId && <FiCheck size={12} />}
                                <Text fontSize="sm" noOfLines={1}>{styles[id].name}</Text>
                            </HStack>
                        ))}
                    </Box>
                    <HStack>
                        <Tooltip label={t('manage.newStyleCopiesTheCurrentOne')}>
                            <IconButton icon={<FiPlus />} size="sm" aria-label={t('manage.newStyle')} onClick={handleNew} />
                        </Tooltip>
                        <Tooltip label={isStandard ? 'The Standard style cannot be deleted'
                            : activeStyleId === selected.id ? 'Set another style current first' : 'Delete style'}>
                            <span>
                                <IconButton
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    aria-label={t('manage.deleteStyle')}
                                    isDisabled={isStandard || activeStyleId === selected.id}
                                    onClick={() => {
                                        deleteStyle(selected.id);
                                        setSelectedId(activeStyleId);
                                    }}
                                />
                            </span>
                        </Tooltip>
                        <Button size="sm" variant={activeStyleId === selected.id ? 'solid' : 'ghost'}
                            onClick={() => setActiveStyle(selected.id)}
                        >{t('manage.setCurrent')}</Button>
                    </HStack>
                </VStack>

                {/* Property editor column */}
                <VStack spacing={2} align="stretch" flex="1" pl={4} borderLeftWidth="1px" borderColor="var(--color-border)">
                    <HStack justify="space-between">
                        <Text fontWeight="semibold">{selected.name === 'Standard' ? 'Standard' : 'Style:'}</Text>
                        <Input size="sm" maxW="200px" value={selected.name} bg="var(--color-bg-primary)"
                            borderColor="var(--color-border)" isDisabled={isStandard}
                            onChange={(e) => patch({ name: e.target.value })}
                        />
                    </HStack>
                    <Divider />
                    <Text fontSize="xs" textTransform="uppercase" color="var(--color-text-secondary)">{t('manage.text')}</Text>
                    <NumberField label={t('manage.textHeight')} value={selected.textHeight} onChange={(v) => patch({ textHeight: v })} />
                    <ColorField label={t('manage.textColor')} value={selected.textColor} onChange={(v) => patch({ textColor: v })} />
                    <NumberField label={t('manage.gapFromLine')} value={selected.textGap} onChange={(v) => patch({ textGap: v })} />
                    <Divider />
                    <Text fontSize="xs" textTransform="uppercase" color="var(--color-text-secondary)">{t('manage.lines')}</Text>
                    <ColorField label={t('manage.lineColor')} value={selected.lineColor} onChange={(v) => patch({ lineColor: v })} />
                    <NumberField label={t('manage.lineWeight')} value={selected.lineWeight} onChange={(v) => patch({ lineWeight: v })} step={1} />
                    <NumberField label={t('manage.extensionOffset')} value={selected.extensionOffset} onChange={(v) => patch({ extensionOffset: v })} />
                    <NumberField label={t('manage.extensionOvershoot')} value={selected.extensionOvershoot} onChange={(v) => patch({ extensionOvershoot: v })} />
                    <Divider />
                    <Text fontSize="xs" textTransform="uppercase" color="var(--color-text-secondary)">{t('manage.arrowsAmpUnits')}</Text>
                    <HStack justify="space-between">
                        <Text fontSize="sm" color="var(--color-text-secondary)">{t('manage.arrowType')}</Text>
                        <Select size="sm" maxW="140px" value={selected.arrowType}
                            bg="var(--color-bg-primary)" borderColor="var(--color-border)"
                            onChange={(e) => patch({ arrowType: e.target.value as DimensionStyle['arrowType'] })}
                        >
                            {ARROW_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </Select>
                    </HStack>
                    <NumberField label={t('manage.arrowSize')} value={selected.arrowSize} onChange={(v) => patch({ arrowSize: v })} />
                    <NumberField label={t('manage.precisionDecimals')} value={selected.precision} onChange={(v) => patch({ precision: Math.max(0, Math.round(v)) })} step={1} />
                    <HStack justify="space-between">
                        <Text fontSize="sm" color="var(--color-text-secondary)">{t('manage.suffix')}</Text>
                        <Input size="sm" maxW="110px" value={selected.suffix} bg="var(--color-bg-primary)"
                            borderColor="var(--color-border)" fontFamily="mono"
                            onChange={(e) => patch({ suffix: e.target.value })}
                        />
                    </HStack>
                    <NumberField label={t('manage.measureScale')} value={selected.scale} onChange={(v) => patch({ scale: v })} step={1} />
                </VStack>
            </HStack>
        </ManageDialogFrame>
    );
}
